import type { BlockType } from '../types'

// ── Block transition config ───────────────────────────────────────────────────

export const BLOCK_TRANSITION_CONFIG: Record<BlockType, { label: string; message: string }> = {
  new_words:      { label: 'New Words', message: "Let's learn some new vocabulary" },
  review:         { label: 'Review',    message: "Time to review words you've seen" },
  mixed_practice: { label: 'Practice',  message: "Let's reinforce what you've learned" },
}

// ── Shake / animation keyframes ───────────────────────────────────────────────

export function injectShakeKeyframes() {
  // Versioned id — bump when this stylesheet's contents change so dev
  // sessions with a stale style tag from a prior version pick up the new
  // rules on next mount instead of being silently ignored by the
  // early-return guard.
  //   v2 — added the (defunct) type="password" un-mask attempt (Prompt J).
  //   v3 — added the learnCaretBlink keyframe for the visible-text overlay
  //        and dropped the un-mask rule. Diagnostic on Chrome for Android
  //        confirmed -webkit-text-security:none is ignored on real
  //        type="password" inputs (the CSS rule was winning the cascade —
  //        the browser just doesn't apply it to password fields). The
  //        overlay-based approach replaces it; see LearnPage.tsx (Prompt K).
  //   v4 — dropped learnCaretBlink: the synthetic caret is now static per
  //        the ADR-014 caret-model amendment (2026-07-15).
  //   v5 — added an inert ::selection rule as defense-in-depth against a
  //        transient one-frame flash of native bullet glyphs during range
  //        selection, ahead of JS collapsing it (ADR-014, second correction,
  //        2026-07-15).
  if (document.getElementById('learn-shake-kf-v5')) return
  document.getElementById('learn-shake-kf')?.remove()
  document.getElementById('learn-shake-kf-v2')?.remove()
  document.getElementById('learn-shake-kf-v3')?.remove()
  document.getElementById('learn-shake-kf-v4')?.remove()
  const s = document.createElement('style')
  s.id = 'learn-shake-kf-v5'
  s.textContent = `
    @keyframes learnShake {
      0%,100% { transform: translateX(0); }
      18%     { transform: translateX(-7px); }
      36%     { transform: translateX(7px); }
      54%     { transform: translateX(-4px); }
      72%     { transform: translateX(4px); }
      90%     { transform: translateX(-2px); }
    }
    @keyframes blockTransitionFadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes blockTransitionPop {
      0%   { transform: scale(0.6); opacity: 0; }
      60%  { transform: scale(1.15); opacity: 1; }
      100% { transform: scale(1); }
    }
    @keyframes blockTransitionSlideUp {
      from { opacity: 0; transform: translateY(12px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes learnSpin {
      to { transform: rotate(360deg); }
    }
    /* Learn-mode answer input — IME suggestion suppression via the
       unmasked-password-field technique (Prompt I, refined in Prompt K).
       The IME-suppression part still works: mobile IMEs unconditionally
       disable the predictive strip, autocorrect, autocapitalize, and
       learning-from-input on type="password". The un-masking part of
       Prompt J (-webkit-text-security:none) does NOT work on Chromium
       Android — the property is ignored on real password fields. The
       letters the user types are now drawn by the visible-text overlay
       in LearnPage.tsx instead; the input itself is transparent. */
    input[type="password"].learn-answer-input {
      letter-spacing: normal;
      font-family: var(--font-content);
    }
    /* Defense-in-depth (ADR-014, second correction): JS collapses any range
       selection to a caret on the next selectionchange/select tick, but that
       still leaves a possible single-frame window where the browser paints
       the native bullet-glyph selection highlight before the collapse runs.
       Making the selection itself visually inert closes that window
       regardless of timing — background and text color both fully
       transparent, so even an un-collapsed selection paints nothing. */
    input[type="password"].learn-answer-input::selection {
      background: transparent;
      color: transparent;
    }
  `
  document.head.appendChild(s)
}

// ── Fill-in-the-blank helpers ─────────────────────────────────────────────────

/**
 * One piece of a tokenized fill-in-the-blank sentence.
 *  - `text`   — verbatim segment between blanks (rendered as-is).
 *  - `blank`  — a position where the target term appears in the sentence.
 *               `match` carries the original surface text from the sentence
 *               (preserves casing per occurrence: "Correct" vs "correct").
 */
export type FibSegment =
  | { kind: 'text'; text: string }
  | { kind: 'blank'; match: string }

/**
 * Tokenizes `sentence` against `term`, producing an alternating sequence of
 * text and blank segments. Every whole-word occurrence of the term becomes a
 * coordinated blank in the rendered sentence — same hint, same reveal, same
 * color in all positions.
 *
 * Matching strategy (in order; whichever step finds something wins):
 *  0. Backend-authored placeholder `_{2,}` — used as a single blank position.
 *  1. Whole-word exact match (case-insensitive). All occurrences become
 *     blanks. Term "correct" matches "correct" twice but NOT "incorrect".
 *
 * There is deliberately no third strategy. A prefix / lemma-initial fallback
 * (`\bterm\w*`) used to sit here and is retired by ADR-068 Decision 6: it
 * produced a blank over an *inflected* surface ("stayed") while grading compares
 * against the Term ("Stay"), so the learner typed what the sentence asked for
 * and was marked wrong — a defect they cannot diagnose. Worse, whether it fired
 * depended on English morphology rather than on anything the author did: a
 * regular plural was caught and mis-graded, an irregular one degraded cleanly.
 *
 * This now mirrors the persist gate `term_occurs_as_whole_word`
 * (`backend/app/services/ai/card_generator.py`) exactly — whole word,
 * case-insensitive, no prefix and no lemma branch — so the rule that decides
 * whether an Example side is *stored* and the rule that decides whether a blank
 * is *built* can never disagree. Do not add inflection tolerance here; the fix
 * for a mismatched form is to edit the Term, which the learner can do.
 *
 * Returns null if the term is empty or no whole-word occurrence exists. Callers
 * must treat null as "this example cannot be shown on a face that asks for its
 * answer" (ADR-068 Decision 7), not merely as "render it plain".
 */
export function buildFillInBlank(sentence: string, term: string): FibSegment[] | null {
  if (!term.trim()) return null
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // 0. Explicit underscore placeholder (first run of {2+} underscores).
  // We store the TERM as the blank's `match`, not the underscore string. The
  // raw underscores are only an authoring hint to locate the blank; once we
  // know the position, the canonical surface text we want to reveal at
  // reveal='full' is the term itself. Without this, the 'full' reveal in
  // FillInBlankSentence would render the underscores tinted green/red
  // instead of the actual word (Prompt E bug).
  const placeholderMatch = /(_{2,})/.exec(sentence)
  if (placeholderMatch) {
    const i = placeholderMatch.index
    return compactSegments([
      { kind: 'text', text: sentence.slice(0, i) },
      { kind: 'blank', match: term },
      { kind: 'text', text: sentence.slice(i + placeholderMatch[1].length) },
    ])
  }

  // 1. Whole-word exact matches (all occurrences, case-insensitive).
  // This is the only matching strategy. See the doc comment above for why the
  // prefix fallback that used to follow it is gone (ADR-068 Decision 6).
  const exactSegments = sliceByRegex(sentence, new RegExp(`\\b(${escaped})\\b`, 'gi'))
  if (exactSegments) return exactSegments

  return null
}

function sliceByRegex(sentence: string, regex: RegExp): FibSegment[] | null {
  const segments: FibSegment[] = []
  let lastIdx = 0
  let m: RegExpExecArray | null
  let found = false
  while ((m = regex.exec(sentence)) !== null) {
    found = true
    if (m.index > lastIdx) segments.push({ kind: 'text', text: sentence.slice(lastIdx, m.index) })
    segments.push({ kind: 'blank', match: m[1] })
    lastIdx = m.index + m[1].length
    // Guard against zero-width regex matches (would loop forever).
    if (m[1].length === 0) regex.lastIndex++
  }
  if (!found) return null
  if (lastIdx < sentence.length) segments.push({ kind: 'text', text: sentence.slice(lastIdx) })
  return segments
}

function compactSegments(segments: FibSegment[]): FibSegment[] {
  return segments.filter((s) => s.kind !== 'text' || s.text.length > 0)
}

// ── Word-boundary truncation ──────────────────────────────────────────────────

/**
 * Truncates `text` to at most `maxLength` characters, cutting at the nearest
 * preceding word boundary (whitespace) and appending an ellipsis. Returns the
 * original text unchanged when it already fits.
 */
export function truncateAtWordBoundary(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  const slice = text.slice(0, maxLength)
  const lastSpace = slice.lastIndexOf(' ')
  const cut = lastSpace > maxLength * 0.5 ? slice.slice(0, lastSpace) : slice
  return cut.replace(/[\s.,;:!?\-—]+$/, '') + '…'
}

// ── Daily correct-answer counter (localStorage) ───────────────────────────────

const DAILY_KEY = 'cs_daily_correct'

export function getTodayCorrect(): number {
  try {
    const raw = localStorage.getItem(DAILY_KEY)
    if (!raw) return 0
    const { date, count } = JSON.parse(raw) as { date: string; count: number }
    return date === new Date().toDateString() ? count : 0
  } catch { return 0 }
}

export function addTodayCorrect(n: number): number {
  const today = new Date().toDateString()
  let prev = 0
  try {
    const raw = localStorage.getItem(DAILY_KEY)
    if (raw) {
      const { date, count } = JSON.parse(raw) as { date: string; count: number }
      if (date === today) prev = count
    }
  } catch { /* ignore */ }
  const next = prev + n
  localStorage.setItem(DAILY_KEY, JSON.stringify({ date: today, count: next }))
  return next
}
